import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useRouter } from '@tanstack/react-router';
import { WeekMealPlanView } from './WeekMealPlanView';
import { RecipesView } from './RecipesView';
import { MealLogView } from './MealLogView';
import { GenerateMealPlanForm } from './GenerateMealPlanForm';
// M2.34.B — tabs nuevos: lista de la compra + preparaciones.
import { ShoppingListView } from './ShoppingListView';
import { PrepTasksView } from './PrepTasksView';
// M2.36 — catálogo de recetas + sistema PILAS.
import { RecipeCatalogView } from './RecipeCatalogView';
import { PilasInfoView } from './PilasInfoView';

/**
 * Página `/nutricion`. 4 tabs: plan semanal / recetas / mi diario / generar.
 *
 * Decisión: NO usamos rutas anidadas (`/nutricion/plan`, `/nutricion/recetas`)
 * para evitar duplicar el árbol y el chrome del layout. En su lugar usamos un
 * query param `?tab=...` controlado:
 *
 * - Permite enlaces compartibles ("ver el tab recetas") sin proliferación de
 *   rutas.
 * - El estado del tab se sincroniza con la URL vía `router.navigate({ search })`
 *   y se lee con `router.state.location.search`. Si entra alguien sin `?tab=…`,
 *   asumimos el primer tab.
 * - Cambiar de tab NO desmonta los tabs vecinos del DOM porque cada vista
 *   maneja su propio fetch con TanStack Query (cache compartido), pero sólo
 *   renderizamos el tab activo para simplificar accesibilidad y reducir trabajo.
 *
 * El patrón `role="tablist"`/`role="tab"`/`role="tabpanel"` cumple WCAG 2.1
 * (los tabs son botones nativos, así que la navegación con teclado y screen
 * readers funciona out-of-the-box).
 */

// M2.34.B — añadidos 'compra' y 'preparaciones' al FINAL del array. El orden
// se mantiene compatible con tests/links pre-existentes.
// M2.36 — añadidos 'catalogo' y 'pilas' al final.
const TAB_IDS = [
  'plan',
  'recetas',
  'diario',
  'generar',
  'compra',
  'preparaciones',
  'catalogo',
  'pilas',
] as const;
type TabId = (typeof TAB_IDS)[number];

const TAB_LABELS: Record<TabId, string> = {
  plan: 'Plan semanal',
  recetas: 'Recetas',
  diario: 'Mi diario',
  generar: 'Generar plan',
  compra: 'Lista de compra',
  preparaciones: 'Preparaciones',
  catalogo: 'Catálogo',
  pilas: 'PILAS',
};

function isTabId(v: unknown): v is TabId {
  return typeof v === 'string' && (TAB_IDS as readonly string[]).includes(v);
}

export function NutritionPage(): ReactElement {
  const router = useRouter();
  // Leemos `?tab=` de la URL. Si no está o no es un valor válido, default = 'plan'.
  const search = router.state.location.search as Record<string, unknown> | undefined;
  const rawTab = search?.tab;
  const activeTab: TabId = isTabId(rawTab) ? rawTab : 'plan';

  // Estado local mirror — evita parpadeo cuando el router actualiza después.
  const [localTab, setLocalTab] = useState<TabId>(activeTab);
  useEffect(() => {
    setLocalTab(activeTab);
  }, [activeTab]);

  const setTab = useCallback(
    (next: TabId) => {
      setLocalTab(next);
      // Mantenemos resto de search params (por si en el futuro hay `weekStart`,
      // etc.) y sólo modificamos `tab`.
      router.navigate({
        to: '/nutricion',
        search: (prev: Record<string, unknown> | undefined) => ({
          ...(prev ?? {}),
          tab: next,
        }),
        replace: true,
      });
    },
    [router],
  );

  return (
    <section aria-labelledby="nutrition-title" className="mx-auto max-w-6xl space-y-6">
      <header>
        <h1 id="nutrition-title" className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
          Nutrición
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Plan semanal de comidas, recetas y diario alimenticio.
        </p>
      </header>

      <TabBar activeTab={localTab} onChange={setTab} />

      <TabPanels activeTab={localTab} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------

function TabBar({
  activeTab,
  onChange,
}: {
  activeTab: TabId;
  onChange: (next: TabId) => void;
}): ReactElement {
  return (
    <div
      role="tablist"
      aria-label="Secciones de Nutrición"
      className="flex flex-wrap gap-1 rounded-md border border-slate-200 bg-white p-1 shadow-sm"
    >
      {TAB_IDS.map((id) => {
        const isActive = id === activeTab;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            id={`tab-${id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(id)}
            className={
              isActive
                ? 'rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2'
                : 'rounded-md px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2'
            }
          >
            {TAB_LABELS[id]}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab panels
// ---------------------------------------------------------------------------

function TabPanels({ activeTab }: { activeTab: TabId }): ReactElement {
  const initialWeek = useMemo(() => startOfIsoWeekISO(new Date()), []);
  // Estado compartido: la semana mostrada en el plan; el form también la usa
  // como default para evitar fricción si el usuario decide generar uno nuevo
  // tras navegar por el calendario.
  const [weekStartISO, setWeekStartISO] = useState<string>(initialWeek);

  return (
    <>
      <div
        role="tabpanel"
        id="tabpanel-plan"
        aria-labelledby="tab-plan"
        hidden={activeTab !== 'plan'}
      >
        {activeTab === 'plan' && (
          <WeekMealPlanView
            weekStartISO={weekStartISO}
            onWeekChange={setWeekStartISO}
          />
        )}
      </div>

      <div
        role="tabpanel"
        id="tabpanel-recetas"
        aria-labelledby="tab-recetas"
        hidden={activeTab !== 'recetas'}
      >
        {activeTab === 'recetas' && <RecipesView />}
      </div>

      <div
        role="tabpanel"
        id="tabpanel-diario"
        aria-labelledby="tab-diario"
        hidden={activeTab !== 'diario'}
      >
        {activeTab === 'diario' && <MealLogView />}
      </div>

      <div
        role="tabpanel"
        id="tabpanel-generar"
        aria-labelledby="tab-generar"
        hidden={activeTab !== 'generar'}
      >
        {activeTab === 'generar' && (
          <GenerateMealPlanForm
            defaultWeekStartISO={weekStartISO}
            onSuccess={(newWeekStartISO) => {
              setWeekStartISO(newWeekStartISO);
            }}
          />
        )}
      </div>

      {/* M2.34.B — nuevos paneles. */}
      <div
        role="tabpanel"
        id="tabpanel-compra"
        aria-labelledby="tab-compra"
        hidden={activeTab !== 'compra'}
      >
        {activeTab === 'compra' && (
          <ShoppingListView weekStartISO={weekStartISO} />
        )}
      </div>

      <div
        role="tabpanel"
        id="tabpanel-preparaciones"
        aria-labelledby="tab-preparaciones"
        hidden={activeTab !== 'preparaciones'}
      >
        {activeTab === 'preparaciones' && (
          <PrepTasksView weekStartISO={weekStartISO} />
        )}
      </div>

      {/* M2.36 — nuevos paneles. */}
      <div
        role="tabpanel"
        id="tabpanel-catalogo"
        aria-labelledby="tab-catalogo"
        hidden={activeTab !== 'catalogo'}
      >
        {activeTab === 'catalogo' && <RecipeCatalogView />}
      </div>

      <div
        role="tabpanel"
        id="tabpanel-pilas"
        aria-labelledby="tab-pilas"
        hidden={activeTab !== 'pilas'}
      >
        {activeTab === 'pilas' && <PilasInfoView />}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Devuelve el ISO del lunes (00:00 UTC) que contiene `date`. Idéntica
 * implementación que `dashboard/WeekNavigator.startOfIsoWeekISO`; la
 * duplicamos para no acoplar la feature de nutrición a un módulo que vive en
 * `features/dashboard`. Si en el futuro alguien extrae un `lib/dates.ts`
 * compartido, este punto es candidato a refactor.
 */
function startOfIsoWeekISO(date: Date): string {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayOfWeek = d.getUTCDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  d.setUTCDate(d.getUTCDate() + diff);
  // M2.30 — Devolvemos YYYY-MM-DD (no ISO 8601 completo). El backend
  // nutrition espera regex /^\d{4}-\d{2}-\d{2}$/. Antes devolvíamos
  // .toISOString() y todos los GET /api/nutrition/week devolvían 400.
  return d.toISOString().slice(0, 10);
}
